import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Download, Loader2 } from "lucide-react";
import { API, useAuth } from "../AuthContext";
import { exportToCSV, formatDate } from "../utils";

export default function MessagesPage() {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, { headers: authHeaders() });
      setItems(data);
    } catch {
      toast.error("Erro ao carregar mensagens");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Apagar esta mensagem?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeaders() });
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success("Mensagem apagada");
    } catch {
      toast.error("Erro ao apagar");
    }
  };

  const exportCSV = () =>
    exportToCSV("mensagens-quinta-oliveirinha.csv", items, [
      { label: "Data", value: (r) => formatDate(r.created_at) },
      { label: "Nome", value: "name" },
      { label: "Email", value: "email" },
      { label: "Telefone", value: "phone" },
      { label: "Assunto", value: "subject" },
      { label: "Mensagem", value: "message" },
    ]);

  return (
    <div data-testid="admin-messages-page" className="bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-[#D4C4A8]/40">
        <h2 className="text-lg font-semibold text-[#2C2C2C]">
          Mensagens de Contacto <span className="text-sm text-[#2C2C2C]/50 ml-2">({items.length})</span>
        </h2>
        <button
          onClick={exportCSV}
          disabled={!items.length}
          data-testid="messages-export-csv"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors disabled:opacity-50"
        >
          <Download size={14} />
          CSV
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#3A4A38]" />
        </div>
      ) : items.length === 0 ? (
        <p className="p-8 text-center text-[#2C2C2C]/60 text-sm">Sem mensagens.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="messages-table">
            <thead className="bg-[#F3F1EB]/60">
              <tr className="text-left text-xs uppercase tracking-wider text-[#2C2C2C]/60">
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Contacto</th>
                <th className="px-4 py-3">Assunto</th>
                <th className="px-4 py-3">Mensagem</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D4C4A8]/30">
              {items.map((m) => (
                <tr key={m.id} className="hover:bg-[#F3F1EB]/40">
                  <td className="px-4 py-3 text-xs text-[#2C2C2C]/70 whitespace-nowrap">{formatDate(m.created_at)}</td>
                  <td className="px-4 py-3 font-medium">{m.name}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{m.email}</div>
                    {m.phone && <div className="text-[#2C2C2C]/60">{m.phone}</div>}
                  </td>
                  <td className="px-4 py-3">{m.subject}</td>
                  <td className="px-4 py-3 max-w-md">
                    <p className="line-clamp-3 text-[#2C2C2C]/80">{m.message}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onDelete(m.id)}
                      data-testid={`message-delete-${m.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
